import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  ProgressBar,
  Badge,
  Button,
  Card,
  Modal,
} from "react-bootstrap";
import { cancelOrder, trackOrder } from "../../features/buyer/buyerSlice";
import Loader from "../../components/common/Loader";
import { timeAgo } from "../../utils/timeAgo";
import {
  FaTruck,
  FaClock,
  FaCheck,
  FaHandshake,
  FaTimes,
  FaBoxOpen,
  FaBan,
} from "react-icons/fa";
import { capitalizeFirst } from "../../utils/stringUtils";
import { formatCurrency } from "../../utils/price";
import AppButton from "../../components/buyer/AppButton";
import theme from "../../styles/theme/theme";
import { showAppMessageToast } from "../../utils/toastUtils";

const OrderTrack = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const { orderTrack, orderLoading, orderError } = useSelector(
    (state) => state.buyer
  );

  useEffect(() => {
    if (id) dispatch(trackOrder(id));

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch, id]);

  const toggleCancelModal = () => setShowCancelModal(!showCancelModal);

  const confirmCancelOrder = async () => {
    try {
      await dispatch(cancelOrder(id)).unwrap();
      showAppMessageToast({
        type: "success",
        title: "Order Cancelled",
        message: "Your order has been cancelled successfully.",
      });
      // Refresh order details to update UI
      dispatch(trackOrder(id));
      setShowCancelModal(false);
    } catch (err) {
      showAppMessageToast({
        type: "error",
        title: "Cancellation Failed",
        message: err || "Failed to cancel order. Please try again.",
      });
    }
  };

  if (orderLoading) return <Loader />;
  if (orderError) return <div className="text-danger">{orderError}</div>;
  if (!orderTrack?.data) return null;

  const {
    tracking_number,
    shipping_partner,
    current_status,
    estimated_delivery,
    order_summary,
  } = orderTrack.data;

  const {
    created_at,
    payment_released_at,
    delivered_at,
    shipping_cost,
    seller,
    status,
    id: orderId,
  } = order_summary || {};

  const currentStatus = (current_status || status || "").toLowerCase();

  let steps = [];

  if (currentStatus === "cancelled") {
    steps = [
      { label: "Pending", icon: <FaClock />, date: created_at },
      { label: "Cancelled", icon: <FaBan />, date: null },
    ];
  } else if (currentStatus === "rejected") {
    steps = [
      { label: "Pending", icon: <FaClock />, date: created_at },
      { label: "Matched", icon: <FaHandshake />, date: null },
      { label: "Rejected", icon: <FaTimes />, date: null },
    ];
  } else {
    steps = [
      { label: "Pending", icon: <FaClock />, date: created_at },
      { label: "Matched", icon: <FaHandshake />, date: null },
      { label: "Accepted", icon: <FaCheck />, date: payment_released_at },
      { label: "Shipped", icon: <FaTruck />, date: null },
      { label: "Completed", icon: <FaBoxOpen />, date: delivered_at },
    ];
  }

  const activeStepIndex = steps.findIndex(
    (step) => step.label.toLowerCase() === currentStatus
  );

  const progressPercentage =
    ((activeStepIndex === -1 ? 0 : activeStepIndex + 1) / steps.length) * 100;

  return (
    <Container className="py-4 px-3 px-md-5">
      <h2 className="mb-4">Track Your Order</h2>

      {/* Order Info */}
      <Card className="p-4 mb-4 shadow-sm">
        <Row>
          <Col md={6}>
            <h5>Order ID: #{orderId}</h5>
            <p>
              <strong>Status:</strong>{" "}
              <Badge bg={statusColor(current_status || status)}>
                {capitalizeFirst(current_status || status)}
              </Badge>
            </p>
            <p>
              <strong>Tracking Number:</strong>{" "}
              {tracking_number || "Not Assigned"}
            </p>
            <p>
              <strong>Shipping Partner:</strong>{" "}
              {shipping_partner?.name || "Not Assigned"}
            </p>
            <p>
              <strong>Estimated Delivery:</strong>{" "}
              {estimated_delivery || "N/A"}
            </p>
            <p>
              <strong>Shipping Cost:</strong> {formatCurrency(shipping_cost)}
            </p>
          </Col>
          <Col md={6}>
            <h6>Shipping To:</h6>
            <p>{seller?.company_name}</p>
            <p>
              {seller?.addressLine1}, {seller?.city}
            </p>
            <p>
              {seller?.state} - {seller?.postal_code}
            </p>
          </Col>
        </Row>
      </Card>

      {/* Progress Section */}
      <Card className="p-4 mb-4 shadow-sm">
        <h5 className="mb-3">Progress</h5>

        {isMobile ? (
          /* Vertical layout */
          <div className="d-flex">
            {/* Vertical progress bar */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginRight: "1rem",
              }}
            >
              <div
                style={{
                  width: "8px",
                  height: "100%",
                  backgroundColor: "#e9ecef",
                  borderRadius: "4px",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    height: `${progressPercentage}%`,
                    width: "100%",
                    backgroundColor: "#0dcaf0",
                    transition: "height 0.3s ease",
                  }}
                />
              </div>
            </div>

            {/* Steps */}
            <div style={{ flex: 1 }}>
              {steps.map((step, index) => (
                <div key={index} className="mb-4">
                  <div
                    style={{
                      color: index <= activeStepIndex ? "green" : "gray",
                      fontSize: "1.2rem",
                      marginBottom: 4,
                    }}
                  >
                    {step.icon}
                  </div>
                  <div style={{ fontWeight: 600 }}>{step.label}</div>
                  <div style={{ fontSize: "0.85rem" }}>
                    {step.date
                      ? timeAgo(step.date)
                      : index < activeStepIndex
                        ? "Done"
                        : index === activeStepIndex
                          ? "In Progress"
                          : "Pending"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Horizontal layout */
          <>
            <ProgressBar
              now={progressPercentage}
              variant="info"
              className="mb-4"
            />
            <Row>
              {steps.map((step, index) => (
                <Col key={index} className="text-center">
                  <div
                    style={{
                      color: index <= activeStepIndex ? "green" : "gray",
                      fontSize: "1.2rem",
                      marginBottom: 6,
                    }}
                  >
                    {step.icon}
                  </div>
                  <div style={{ fontWeight: 600 }}>{step.label}</div>
                  <div style={{ fontSize: "0.85rem" }}>
                    {step.date
                      ? timeAgo(step.date)
                      : index < activeStepIndex
                        ? "Done"
                        : index === activeStepIndex
                          ? "In Progress"
                          : "Pending"}
                  </div>
                </Col>
              ))}
            </Row>
          </>
        )}
      </Card>

      {/* Cancel Button */}
      <div className="text-center mt-4">
        <Button
          variant="danger"
          disabled={(current_status || status) !== "pending"}
          onClick={toggleCancelModal}
        >
          Cancel Order
        </Button>
      </div>

      {/* Confirmation Modal */}
      <Modal show={showCancelModal} onHide={toggleCancelModal} centered>
        {/* Header */}
        <Modal.Header
          style={{
            backgroundColor: theme.palette.background.paper,
            borderBottom: `2px solid ${theme.palette.secondary.main}`,
          }}
        >
          <Modal.Title
            style={{
              fontWeight: 600,
              fontSize: "1.25rem",
              color: theme.palette.text.primary,
            }}
          >
            Cancel Order
          </Modal.Title>
        </Modal.Header>

        {/* Body */}
        <Modal.Body style={{ backgroundColor: theme.palette.background.paper }}>
          <div
            className="mb-3"
            style={{ fontSize: "0.95rem", color: theme.palette.text.secondary }}
          >
            Are you sure you want to cancel this order? This action cannot be
            undone.
          </div>
        </Modal.Body>

        {/* Footer */}
        <Modal.Footer
          style={{
            backgroundColor: theme.palette.background.paper,
            borderTop: `2px solid ${theme.palette.secondary.main}`,
          }}
        >
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <AppButton
              variant="outlined"
              onClick={toggleCancelModal}
              style={{ marginRight: 12 }}
            >
              No, Keep Order
            </AppButton>
            <AppButton
              variant="contained"
              color={theme.palette.error.main}
              onClick={confirmCancelOrder}
            >
              Yes, Cancel Order
            </AppButton>
          </div>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

const statusColor = (status) => {
  switch ((status || "").toLowerCase()) {
    case "accepted":
      return "success";
    case "pending":
      return "warning";
    case "matched":
      return "info";
    case "shipped":
      return "primary";
    case "completed":
      return "success";
    case "rejected":
    case "cancelled":
      return "danger";
    default:
      return "secondary";
  }
};

export default OrderTrack;
